import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, Clock, MessageCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { siteInfo } from '../data/mockData';

const HomeNew = () => {
  const [openFaq, setOpenFaq] = useState(null);

  // Atualizar título da página
  useEffect(() => {
    document.title = 'Funerária RJ 24 Horas | Atendimento no Rio de Janeiro';
    
    // Meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Funerária RJ 24 horas. Atendimento funerário no Rio de Janeiro para remoção, funeral, sepultamento, cremação e traslado. Entre em contato.';
  }, []);

  const servicosFunerarios = [
    'Atendimento funerário 24 horas',
    'Remoção e transporte funerário',
    'Urnas funerárias',
    'Preparação e conservação',
    'Organização de velório',
    'Sepultamento',
    'Cremação',
    'Documentação funerária',
    'Traslado nacional',
    'Traslado internacional',
    'Repatriação funerária',
    'Coroa de flores'
  ];

  const faqItems = [
    {
      question: 'A funerária funciona 24 horas?',
      answer: 'Sim. O atendimento funerário está disponível 24 horas para orientar as famílias e iniciar os procedimentos necessários.'
    },
    {
      question: 'O que fazer quando ocorre um falecimento?',
      answer: 'Entre em contato com nossa equipe. Vamos verificar a situação e orientar sobre documentação, remoção e os próximos procedimentos.'
    },
    {
      question: 'Quanto custa um funeral no Rio de Janeiro?',
      answer: 'O preço depende dos serviços necessários e das opções escolhidas pela família. Entre em contato para receber informações sobre os valores disponíveis.'
    },
    {
      question: 'Vocês realizam cremação?',
      answer: 'Prestamos assistência funerária para cremação e orientamos sobre a documentação e os requisitos aplicáveis ao caso.'
    },
    {
      question: 'Vocês fazem traslado para outros estados?',
      answer: 'Prestamos assistência para traslados nacionais e internacionais. Consulte a disponibilidade para a origem e o destino necessários.'
    },
    {
      question: 'Posso solicitar orçamento antes de contratar?',
      answer: 'Sim. Entre em contato e solicite informações sobre os serviços e respectivos valores antes da contratação.'
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // Schema.org JSON-LD
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "FuneralHome",
    "name": "Funerária Santa Casa Assistencial",
    "alternateName": "Funerária RJ 24 Horas",
    "description": "Atendimento funerário 24 horas no Rio de Janeiro, com suporte para a família em todas as etapas necessárias.",
    "url": "https://funerariasantacasaassistencial.com",
    "telephone": "+55-21-4106-4022",
    "priceRange": "A partir de R$ 1.480",
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Rio de Janeiro",
      "addressRegion": "RJ",
      "addressCountry": "BR"
    },
    "areaServed": {
      "@type": "City",
      "name": "Rio de Janeiro"
    },
    "sameAs": [
      "https://wa.me/552141064022",
      "https://www.instagram.com/santacasa.assistencial"
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-600 via-teal-500 to-teal-700 text-white py-20 md:py-32">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Funerária RJ 24 Horas
            </h1>
            <h2 className="text-2xl md:text-3xl mb-8 text-teal-50 font-light">
              Atendimento Funerário Imediato no Rio de Janeiro
            </h2>
            <p className="text-lg md:text-xl mb-4 max-w-4xl mx-auto leading-relaxed">
              A Funerária Santa Casa Assistencial oferece <strong>atendimento funerário 24 horas no Rio de Janeiro</strong>, com suporte para a família em todas as etapas necessárias.
            </p>
            <p className="text-base md:text-lg mb-8 max-w-4xl mx-auto leading-relaxed text-teal-50">
              Nossa equipe está disponível para orientar sobre remoção, documentação, preparação, velório, sepultamento, cremação e translado, oferecendo atendimento ágil e humanizado.
            </p>
            <p className="text-xl font-semibold mb-8">
              Precisa de uma funerária agora? Entre em contato com nossa equipe 24 horas.
            </p>

            {/* CTAs Principais */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:41064022"
                onClick={() => {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    event: 'phone_click',
                    click_location: 'hero_home'
                  });
                }}
                className="inline-flex items-center justify-center bg-white text-teal-700 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-bold shadow-2xl transition-all duration-300 hover:scale-105"
              >
                <Phone className="w-5 h-5 mr-2" />
                LIGAR AGORA
              </a>
              <a
                href="https://wa.me/552141064022?text=Olá,%20preciso%20de%20atendimento%20funerário"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  window.dataLayer = window.dataLayer || [];
                  window.dataLayer.push({
                    event: 'whatsapp_click',
                    click_location: 'hero_home'
                  });
                }}
                className="inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-bold shadow-2xl transition-all duration-300 hover:scale-105"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                FALAR PELO WHATSAPP
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introdução */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
            Funerária no Rio de Janeiro com Atendimento 24 Horas
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            Sabemos que a contratação de um serviço funerário acontece em um momento delicado e, muitas vezes, exige decisões rápidas.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            Por isso, nossa equipe oferece <strong>atendimento funerário 24 horas no RJ</strong>, orientando a família desde o primeiro contato até a conclusão do serviço.
          </p>
        </div>
      </section>

      {/* Nossos Serviços */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Nossos Serviços Funerários
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
            {servicosFunerarios.map((servico, index) => (
              <div key={index} className="flex items-start space-x-3 bg-teal-50 rounded-lg p-4">
                <CheckCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-1" />
                <span className="text-gray-800">{servico}</span>
              </div>
            ))}
          </div>

          <div className="text-center">
            <a
              href="https://wa.me/552141064022?text=Gostaria%20de%20solicitar%20atendimento%2024%20horas"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'whatsapp_click',
                  click_location: 'servicos_section'
                });
              }}
              className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all duration-300 hover:scale-105 shadow-lg"
            >
              SOLICITAR ATENDIMENTO 24 HORAS
            </a>
          </div>
        </div>
      </section>

      {/* Quanto Custa */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
            Quanto Custa um Funeral no RJ?
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
            O valor de um funeral pode variar de acordo com os serviços escolhidos, cemitério, urna, preparação, transporte e demais necessidades da família.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-8 text-center">
            Temos opções de serviço funerário completo e nossa equipe pode informar os valores antes da contratação.
          </p>

          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 text-center border-4 border-teal-500 mb-8">
            <div className="mb-6">
              <p className="text-lg text-gray-600 mb-4">Funeral Completo a Partir de</p>
              <p className="text-5xl md:text-6xl font-bold text-teal-600 mb-2">
                R$ 1.480<span className="text-3xl">,00</span><span className="text-2xl text-gray-500">*</span>
              </p>
              <p className="text-sm text-gray-600">Consulte as condições e os serviços incluídos no atendimento.</p>
            </div>
            <a
              href="https://wa.me/552141064022?text=Gostaria%20de%20consultar%20o%20valor%20do%20funeral"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'whatsapp_click',
                  click_location: 'preco_section',
                  action: 'consultar_valor'
                });
              }}
              className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all duration-300 hover:scale-105 shadow-lg"
            >
              CONSULTAR VALOR DO FUNERAL
            </a>
          </div>

          <p className="text-sm text-gray-600 text-center">
            *Valores e disponibilidade sujeitos às condições do serviço contratado. Confirme todas as informações com nossa equipe antes da contratação.
          </p>
        </div>
      </section>

      {/* Processo */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
            Serviço Funerário Completo no Rio de Janeiro
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-12 text-center max-w-3xl mx-auto">
            A família não precisa enfrentar todos os procedimentos sozinha.<br/>
            Nossa equipe orienta sobre os documentos e procedimentos necessários e organiza os serviços funerários contratados.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-teal-50 rounded-xl p-8 border-l-4 border-teal-600">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center text-white text-xl font-bold mr-4">
                  1
                </div>
                <h3 className="text-xl font-bold text-gray-900">Entre em contato</h3>
              </div>
              <p className="text-gray-700">Ligue ou fale conosco pelo WhatsApp.</p>
            </div>

            <div className="bg-teal-50 rounded-xl p-8 border-l-4 border-teal-600">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center text-white text-xl font-bold mr-4">
                  2
                </div>
                <h3 className="text-xl font-bold text-gray-900">Informe a situação</h3>
              </div>
              <p className="text-gray-700">Nossa equipe identifica quais serviços e documentos serão necessários.</p>
            </div>

            <div className="bg-teal-50 rounded-xl p-8 border-l-4 border-teal-600">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center text-white text-xl font-bold mr-4">
                  3
                </div>
                <h3 className="text-xl font-bold text-gray-900">Receba a orientação</h3>
              </div>
              <p className="text-gray-700">Explicamos os procedimentos, opções disponíveis e respectivos valores.</p>
            </div>

            <div className="bg-teal-50 rounded-xl p-8 border-l-4 border-teal-600">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center text-white text-xl font-bold mr-4">
                  4
                </div>
                <h3 className="text-xl font-bold text-gray-900">Iniciamos o atendimento</h3>
              </div>
              <p className="text-gray-700">Após a contratação e autorizações necessárias, iniciamos os procedimentos funerários.</p>
            </div>
          </div>

          <div className="text-center">
            <a
              href="https://wa.me/552141064022?text=Preciso%20de%20atendimento%20agora"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'whatsapp_click',
                  click_location: 'processo_section',
                  urgency: 'high'
                });
              }}
              className="inline-block bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all duration-300 hover:scale-105 shadow-lg"
            >
              PRECISO DE ATENDIMENTO AGORA
            </a>
          </div>
        </div>
      </section>

      {/* Serviços Específicos */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Cremação */}
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Cremação no Rio de Janeiro</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Também oferecemos orientação e assistência para cremação no RJ, incluindo os procedimentos funerários e documentação necessária conforme cada caso.
              </p>
              <p className="text-sm text-gray-600 mb-6">
                A possibilidade de cremação depende do cumprimento das exigências legais aplicáveis.
              </p>
              <Link
                to="/cremacao"
                className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-bold transition-all"
              >
                CONSULTAR CREMAÇÃO
              </Link>
            </div>

            {/* Sepultamento */}
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Sepultamento no Rio de Janeiro</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Prestamos assistência para organização de velório e sepultamento, orientando a família sobre os procedimentos e opções disponíveis.
              </p>
              <Link
                to="/funeral"
                className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-bold transition-all mt-10"
              >
                CONSULTAR SEPULTAMENTO
              </Link>
            </div>

            {/* Traslado */}
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Traslado Funerário Nacional e Internacional</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Realizamos assistência para traslado funerário nacional e internacional, incluindo orientação sobre documentação e procedimentos necessários para cada destino.
              </p>
              <p className="text-sm text-gray-600 mb-6">
                Também prestamos assistência em processos de repatriação funerária.
              </p>
              <Link
                to="/translado"
                className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-bold transition-all"
              >
                CONSULTAR TRASLADO
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Regiões Atendidas */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Funerária RJ — Atendimento em Diversas Regiões do Rio de Janeiro
          </h2>
          <p className="text-lg text-gray-700 mb-4 leading-relaxed max-w-3xl mx-auto">
            Atendimento funerário no município do Rio de Janeiro e demais localidades conforme disponibilidade operacional.
          </p>
          <p className="text-lg text-gray-700 mb-8">
            Consulte nossa equipe para confirmar atendimento na sua região.
          </p>
          <div className="flex items-center justify-center space-x-2 mb-8">
            <Clock className="w-6 h-6 text-teal-600" />
            <p className="text-xl font-bold text-teal-600">Atendimento 24 horas.</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:41064022"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'phone_click',
                  click_location: 'regioes_section'
                });
              }}
              className="inline-flex items-center justify-center bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all duration-300 hover:scale-105 shadow-lg"
            >
              <Phone className="w-5 h-5 mr-2" />
              LIGAR PARA A FUNERÁRIA
            </a>
            <a
              href="https://wa.me/552141064022?text=Olá,%20gostaria%20de%20confirmar%20atendimento%20na%20minha%20região"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'whatsapp_click',
                  click_location: 'regioes_section'
                });
              }}
              className="inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-bold transition-all duration-300 hover:scale-105 shadow-lg"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              CHAMAR NO WHATSAPP
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Perguntas Frequentes
          </h2>
          <div className="space-y-4">
            {faqItems.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-bold text-gray-900 pr-4">{faq.question}</h3>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-teal-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-teal-600 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Precisa de uma Funerária no RJ Agora?
          </h2>
          <p className="text-xl mb-8 text-teal-50">
            Nossa equipe está disponível para prestar orientação e iniciar o atendimento funerário.
          </p>
          <p className="text-2xl font-bold mb-2">Funerária Santa Casa Assistencial</p>
          <p className="text-lg mb-8 text-teal-50">Atendimento Funerário 24 Horas — Rio de Janeiro</p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:41064022"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'phone_click',
                  click_location: 'cta_final'
                });
              }}
              className="inline-flex items-center justify-center bg-white text-teal-700 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-bold shadow-2xl transition-all duration-300 hover:scale-105"
            >
              <Phone className="w-5 h-5 mr-2" />
              LIGAR AGORA
            </a>
            <a
              href="https://wa.me/552141064022?text=Preciso%20de%20uma%20funerária%20agora"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'whatsapp_click',
                  click_location: 'cta_final'
                });
              }}
              className="inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-bold shadow-2xl transition-all duration-300 hover:scale-105"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              FALAR PELO WHATSAPP
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomeNew;
